import React from 'react';
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { useEventForm } from '../../hooks/useEventForm';
import type { EventDraft, RetreatEvent } from '../../types/events';
import { isValidISODate } from '../../utils/eventDates';
import { EventCard } from '../EventCard';
import { AdminField, adminInputClass } from './AdminField';
import { PosterDropzone } from './PosterDropzone';
interface EventFormProps {
  event: RetreatEvent | null;
  onSave: (draft: EventDraft) => Promise<unknown>;
  onDelete: () => Promise<void>;
  onDirtyChange: (dirty: boolean) => void;
  onBack: () => void;
}
function formatSavedAt(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}
export function EventForm({
  event,
  onSave,
  onDelete,
  onDirtyChange,
  onBack
}: EventFormProps) {
  const form = useEventForm({
    event,
    onSave,
    onDelete,
    onDirtyChange
  });
  const {
    values,
    errors,
    setField
  } = form;
  const isNew = event === null;
  const isPublished = event?.published ?? false;
  const savedAt = formatSavedAt(event?.updatedAt ?? null);
  const primaryLabel = isPublished ? 'Update live event' : 'Publish';
  const secondaryLabel = isPublished ? 'Move to drafts' : 'Save draft';
  const primaryDisabled = form.busy || isPublished && !form.isDirty;
  const secondaryDisabled = form.busy || !isPublished && !form.isDirty;
  const statusLine = isNew ? 'New event — not on the site yet' : isPublished ? 'Live on the site' : 'Draft — only you can see this';
  const canPreview = values.title.trim().length > 0 && isValidISODate(values.eventDate);
  return <div data-id="element-93" data-mp="2pa2eakd8q64o">
      <button type="button" onClick={onBack} className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-pine transition-colors hover:text-ember lg:hidden" data-id="element-94" data-mp="2palu9vjbykg2">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-95" data-mp="2m7xt66wftrvn" />
        All events
      </button>

      <div className="mb-8 flex flex-col gap-1" data-id="element-96" data-mp="2h9mkhzhvpnf0">
        <h1 className="font-serif text-3xl text-pine md:text-4xl" data-id="element-97" data-mp="2haxqm5ysr79a">
          {event ? event.title : 'New event'}
        </h1>
        <p className="text-sm text-clay/80" data-id="element-98" data-mp="2w4ao183pybrz">
          <span className={isPublished ? 'font-medium text-pine' : ''} data-id="element-99" data-mp="2r6iuk9nfehlo">{statusLine}</span>
          {savedAt && <span data-id="element-100" data-mp="356a5g1inbhvg"> · Last saved {savedAt}</span>}
        </p>
      </div>

      <form onSubmit={submitEvent => {
      submitEvent.preventDefault();
      form.runSave('publish');
    }} noValidate data-id="element-101" data-mp="308ib70uj3jo3">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[13rem_1fr] lg:gap-10" data-id="element-102" data-mp="3ewda4yz4qk9m">
          <div className="space-y-4" data-id="element-103" data-mp="39yk0tx2j5a54">
            <div className="mx-auto w-full max-w-[13rem]" data-id="element-104" data-mp="3nxt7o64uirwf">
              <PosterDropzone value={values.posterUrl} onChange={url => setField('posterUrl', url)} onBusyChange={form.setPosterBusy} disabled={form.pending !== null || form.deleting} data-id="element-105" data-mp="3nycn9g70x47a" />
            </div>
            {values.posterUrl && <AdminField label="Poster description" htmlFor="event-poster-alt" hint="Read aloud to visitors using screen readers." data-id="element-106" data-mp="bc4j80o8eraz">
                <textarea id="event-poster-alt" rows={3} value={values.posterAlt} onChange={changeEvent => setField('posterAlt', changeEvent.target.value)} className={`${adminInputClass} resize-none text-sm`} placeholder={`Poster for ${values.title || 'the event'}`} data-id="element-107" data-mp="2fn6wu44sndn" />
              </AdminField>}
          </div>

          <div className="space-y-5" data-id="element-108" data-mp="30uwk80rduk9u">
            <AdminField label="Tagline" htmlFor="event-tagline" hint="Optional line above the title, e.g. a lyric or theme." data-id="element-109" data-mp="30vy5ey244n5a">
              <input id="event-tagline" value={values.tagline} onChange={changeEvent => setField('tagline', changeEvent.target.value)} className={adminInputClass} data-id="element-110" data-mp="c0zty7z3zl2g" />
            </AdminField>

            <AdminField label="Title" htmlFor="event-title" required error={errors.title} data-id="element-111" data-mp="3ugsec6ewzyq1">
              <input id="event-title" value={values.title} onChange={changeEvent => setField('title', changeEvent.target.value)} aria-invalid={Boolean(errors.title)} aria-describedby={errors.title ? 'event-title-error' : undefined} className={`${adminInputClass} font-serif text-lg`} data-id="element-112" data-mp="3phpb8rihw2kp" />
            </AdminField>

            <AdminField label="Subtitle" htmlFor="event-subtitle" hint="e.g. A Tribute, Workshop, Sauna Night" data-id="element-113" data-mp="1eo3amipx45vb">
              <input id="event-subtitle" value={values.subtitle} onChange={changeEvent => setField('subtitle', changeEvent.target.value)} className={adminInputClass} data-id="element-114" data-mp="19qky6icoz8ik" />
            </AdminField>

            <AdminField label="Performed by / led by" htmlFor="event-performers" data-id="element-115" data-mp="1qz41lwzlqzyd">
              <input id="event-performers" value={values.performers} onChange={changeEvent => setField('performers', changeEvent.target.value)} className={adminInputClass} placeholder="Names separated by ·" data-id="element-116" data-mp="1vy73j8kacq24" />
            </AdminField>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3" data-id="element-117" data-mp="187tfdt4tnbct">
              <AdminField label="Date" htmlFor="event-date" required error={errors.eventDate} data-id="element-118" data-mp="1ca7rzvhwdevn">
                <input id="event-date" type="date" value={values.eventDate} onChange={changeEvent => setField('eventDate', changeEvent.target.value)} aria-invalid={Boolean(errors.eventDate)} aria-describedby={errors.eventDate ? 'event-date-error' : undefined} className={adminInputClass} data-id="element-119" data-mp="2job7jh1dyrtn" />
              </AdminField>
              <AdminField label="Doors" htmlFor="event-doors" data-id="element-120" data-mp="xkru97xcymyj">
                <input id="event-doors" type="time" value={values.doorsTime} onChange={changeEvent => setField('doorsTime', changeEvent.target.value)} className={adminInputClass} data-id="element-121" data-mp="snjbb54dyew8" />
              </AdminField>
              <AdminField label="Show / start" htmlFor="event-show" data-id="element-122" data-mp="engulm7tdrik">
                <input id="event-show" type="time" value={values.showTime} onChange={changeEvent => setField('showTime', changeEvent.target.value)} className={adminInputClass} data-id="element-123" data-mp="9vguntrquylz" />
              </AdminField>
            </div>

            <AdminField label="Address" htmlFor="event-address" data-id="element-124" data-mp="1st3kw7wl6chk">
              <input id="event-address" value={values.address} onChange={changeEvent => setField('address', changeEvent.target.value)} className={adminInputClass} data-id="element-125" data-mp="1xlwrl1bm7uwa" />
            </AdminField>
          </div>
        </div>

        {form.saveError && <div className="mt-8 flex flex-col gap-3 rounded-sm border border-ember/30 bg-cream p-4 sm:flex-row sm:items-center" role="alert" data-id="element-126" data-mp="2cjytz86wuaby">
            <div className="flex flex-1 gap-2.5 text-sm text-clay" data-id="element-127" data-mp="22l99d7nmqbs0">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-none text-ember" aria-hidden="true" data-id="element-128" data-mp="27jkj7idxcma8" />
              <p data-id="element-129" data-mp="r5avmqhib1kg">{form.saveError.message} Your changes are still here.</p>
            </div>
            <button type="button" onClick={() => form.saveError && form.runSave(form.saveError.action)} disabled={form.busy} className="whitespace-nowrap text-sm font-medium text-pine underline decoration-pine/30 underline-offset-4 hover:text-ember disabled:opacity-50" data-id="element-130" data-mp="r7nmjt58sm9n">
              Retry
            </button>
          </div>}

        <div className="mt-8 flex flex-col gap-3 border-t border-pine/15 pt-6 sm:flex-row sm:items-center" data-id="element-131" data-mp="18upsbqikl29v">
          <button type="submit" disabled={primaryDisabled} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm bg-pine px-7 py-3.5 font-medium tracking-wide text-cream transition-colors hover:bg-pineDeep disabled:cursor-not-allowed disabled:opacity-50" data-id="element-132" data-mp="26i6y3yh0wix3">
            {form.pending === 'publish' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-133" data-mp="1rtaingcn03mg" />}
            {form.pending === 'publish' ? 'Publishing…' : primaryLabel}
          </button>
          <button type="button" onClick={() => form.runSave('draft')} disabled={secondaryDisabled} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm border border-pine/30 px-6 py-3.5 font-medium tracking-wide text-pine transition-colors hover:border-pine hover:bg-pine/5 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-134" data-mp="2kh6k0yd5ijpk">
            {form.pending === 'draft' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-135" data-mp="36oaebltfbih5" />}
            {form.pending === 'draft' ? 'Saving…' : secondaryLabel}
          </button>

          {!isNew && !form.confirmingDelete && <button type="button" onClick={() => form.setConfirmingDelete(true)} disabled={form.busy} className="text-sm text-clay/80 transition-colors hover:text-ember disabled:opacity-50 sm:ml-auto" data-id="element-136" data-mp="3ki1rd46d5khm">
              Delete event
            </button>}
        </div>

        {event && form.confirmingDelete && <div className="mt-5 rounded-sm border border-ember/30 bg-cream p-5" role="alertdialog" aria-labelledby="delete-confirm-title" data-id="element-137" data-mp="3ipw5uhhe91qg">
            <p id="delete-confirm-title" className="mb-4 text-sm text-clay" data-id="element-138" data-mp="3msj2cvp0fatz">
              Delete <span className="font-medium text-pine" data-id="element-139" data-mp="3rq2enq25ddja">“{event.title}”</span>?
              {event.published && ' It will disappear from the live site immediately.'} This can’t be undone.
            </p>
            {form.deleteError && <p className="mb-3 text-sm text-ember" role="alert" data-id="element-140" data-mp="5ahn9jclj1sg">
                {form.deleteError}
              </p>}
            <div className="flex gap-3" data-id="element-141" data-mp="ebo9m91xf6tm">
              <button type="button" onClick={form.confirmDelete} disabled={form.deleting} className="inline-flex items-center gap-2 rounded-sm bg-ember px-5 py-2.5 text-sm font-medium text-cream transition-colors hover:bg-ember/90 disabled:opacity-60" data-id="element-142" data-mp="ecqyycjlp17f">
                {form.deleting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-143" data-mp="2l3yseyhvfmld" />}
                {form.deleting ? 'Deleting…' : 'Delete event'}
              </button>
              <button type="button" onClick={() => form.setConfirmingDelete(false)} disabled={form.deleting} className="rounded-sm border border-pine/30 px-5 py-2.5 text-sm font-medium text-pine transition-colors hover:bg-pine/5" data-id="element-144" data-mp="28qbkitzep0nf">
                Keep it
              </button>
            </div>
          </div>}
      </form>

      <section className="mt-14" aria-labelledby="event-preview-heading" data-id="element-145" data-mp="2rj722pobsc7w">
        <h2 id="event-preview-heading" className="mb-4 text-xs font-medium uppercase tracking-[0.2em] text-clay/70" data-id="element-146" data-mp="2mi9j4avhre5c">
          Preview — how visitors will see it
        </h2>
        {canPreview ? <div className="bg-creamDeep p-4 md:p-6" data-id="element-147" data-mp="1b21g68c5e0t5">
            <EventCard event={form.previewDraft} data-id="element-148" data-mp="172aswelty2td" />
          </div> : <div className="rounded-sm border border-dashed border-pine/25 p-8 text-center text-sm font-light text-clay" data-id="element-149" data-mp="18jzfqhhit7cc">
            Add a title and date to see the preview.
          </div>}
      </section>
    </div>;
}